import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings, Check } from 'lucide-react';
import { Button } from './button';
import { motion, AnimatePresence } from 'framer-motion';
import { applyAnalyticsConsent, getConsent } from '../../lib/analytics';

const COOKIE_CONSENT_KEY = 'gmed_cookie_consent';
export const COOKIE_SETTINGS_EVENT = 'gmed:open-cookie-settings';

export const openCookieSettings = () => {
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
};

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = getConsent();
    // Not shown in the admin area: it would sit on top of the save buttons
    const isAdminArea = window.location.pathname.startsWith('/admin');
    if (!consent) {
      if (isAdminArea) return;
      // Show banner after a short delay - no analytics loaded until a choice is made
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
    setPreferences({
      necessary: true,
      analytics: consent.analytics === true,
      marketing: consent.marketing === true,
    });
    applyPreferences(consent);
  }, []);

  useEffect(() => {
    const openSettings = () => {
      const consent = getConsent();
      if (consent) {
        setPreferences({
          necessary: true,
          analytics: consent.analytics === true,
          marketing: consent.marketing === true,
        });
      }
      setShowSettings(true);
      setShowBanner(true);
    };
    window.addEventListener(COOKIE_SETTINGS_EVENT, openSettings);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, openSettings);
  }, []);

  const applyPreferences = (prefs) => {
    // PostHog is initialised only with analytics consent; revoking stops recording and opts out
    applyAnalyticsConsent(prefs);
  };

  const savePreferences = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    applyPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    const allAccepted = { necessary: true, analytics: true, marketing: true };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
  };

  const acceptNecessary = () => {
    const onlyNecessary = { necessary: true, analytics: false, marketing: false };
    setPreferences(onlyNecessary);
    savePreferences(onlyNecessary);
  };

  const saveCustom = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      >
        <div className="max-w-4xl mx-auto">
          <div className="bg-background text-foreground border border-border rounded-xl shadow-2xl overflow-hidden">
            {!showSettings ? (
              // Main Banner
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">Utilizziamo i cookie</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Questo sito utilizza cookie tecnici necessari al funzionamento e cookie analitici 
                      per migliorare la tua esperienza. Puoi scegliere quali cookie accettare.
                      {' '}
                      <a href="/compliance/cookie-policy" className="text-primary hover:underline">
                        Leggi la Cookie Policy
                      </a>
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button onClick={acceptAll} data-testid="cookie-accept-all">
                        <Check className="w-4 h-4 mr-2" />
                        Accetta tutti
                      </Button>
                      <Button variant="outline" onClick={acceptNecessary} data-testid="cookie-accept-necessary">
                        Solo necessari
                      </Button>
                      <Button variant="ghost" onClick={() => setShowSettings(true)} data-testid="cookie-settings">
                        <Settings className="w-4 h-4 mr-2" />
                        Personalizza
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Settings Panel
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Impostazioni Cookie
                  </h3>
                  <Button variant="ghost" size="icon" onClick={() => {
                    setShowSettings(false);
                    if (getConsent()) setShowBanner(false);
                  }} data-testid="cookie-settings-close">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-4 mb-6">
                  {/* Necessary Cookies */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                    <div>
                      <p className="font-medium">Cookie Necessari</p>
                      <p className="text-sm text-muted-foreground">
                        Essenziali per il funzionamento del sito. Non possono essere disattivati.
                      </p>
                    </div>
                    <div className="px-3 py-1 rounded bg-green-500/20 text-green-500 text-sm font-medium">
                      Sempre attivi
                    </div>
                  </div>
                  
                  {/* Analytics Cookies */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                    <div>
                      <p className="font-medium">Cookie Analitici</p>
                      <p className="text-sm text-muted-foreground">
                        Ci aiutano a capire come i visitatori interagiscono con il sito.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                        className="sr-only peer"
                        data-testid="cookie-analytics-toggle"
                      />
                      <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-primary peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ring after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                    </label>
                  </div>
                  
                  {/* Marketing Cookies */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                    <div>
                      <p className="font-medium">Cookie di Marketing</p>
                      <p className="text-sm text-muted-foreground">
                        Utilizzati per mostrare annunci pertinenti ai tuoi interessi.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                        className="sr-only peer"
                        data-testid="cookie-marketing-toggle"
                      />
                      <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-primary peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ring after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowSettings(false);
                      if (getConsent()) setShowBanner(false);
                    }}
                    data-testid="cookie-settings-cancel"
                  >
                    Annulla
                  </Button>
                  <Button onClick={saveCustom} data-testid="cookie-save-preferences">
                    Salva preferenze
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieBanner;
